import type { ReportTable } from '@erp/shared-types';
import Papa from 'papaparse';

import type {
  DownloadCSVOptions,
  MultiTableCSVOptions,
} from '@/utils/csvDownload';

import type { RebateExportCell, RebateExportRow } from './rebateExportTypes';
import { parseRebateExportCell } from './parseRebateExportCell';
import {
  cellFromReportTableCell,
  isCurrencyFormatted,
  kindForNumericHeader,
  unwrapDownloadCsvCell,
} from './reportTableCellExport';

function textCell(value: string): RebateExportCell {
  return { value, kind: 'text' };
}

function emptyCell(): RebateExportCell {
  return { value: null, kind: 'empty' };
}

function titleRow(title: string): RebateExportRow {
  return { role: 'title', cells: [textCell(title)] };
}

function blankRow(): RebateExportRow {
  return { role: 'blank', cells: [] };
}

function headerRow(headers: string[]): RebateExportRow {
  return { role: 'header', cells: headers.map(textCell) };
}

function dataRow(cells: RebateExportCell[]): RebateExportRow {
  return { role: 'data', cells };
}

function defaultRowLabelHeader(table: ReportTable): string {
  return table.title.includes('Supplier') ? 'Supplier' : 'Category';
}

function visibleIndices(
  table: ReportTable,
  visibleColumnIndices?: number[],
): number[] {
  return (
    visibleColumnIndices ??
    Array.from({ length: table.columnHeaders.length }, (_, index) => index)
  );
}

function sumNumericColumn(
  rows: RebateExportRow[],
  columnIndex: number,
): number | null {
  let sum = 0;
  let hasNumeric = false;
  rows.forEach((row) => {
    const cell = row.cells[columnIndex];
    if (cell && typeof cell.value === 'number' && !Number.isNaN(cell.value)) {
      sum += cell.value;
      hasNumeric = true;
    }
  });
  return hasNumeric ? sum : null;
}

export function buildExportRowsFromReportTables(
  tables: MultiTableCSVOptions['tables'],
): RebateExportRow[] {
  const rows: RebateExportRow[] = [];

  tables.forEach((tableSection, tableIndex) => {
    const {
      title,
      table,
      rowLabelHeader,
      visibleColumnIndices,
      includeTotals = true,
    } = tableSection;
    if (tableIndex > 0) {
      rows.push(blankRow());
    }
    rows.push(titleRow(title));
    const columnsToInclude = visibleIndices(table, visibleColumnIndices);
    const headers = [
      rowLabelHeader || defaultRowLabelHeader(table),
      ...columnsToInclude.map(
        (colIndex) => table.columnHeaders[colIndex] ?? '',
      ),
    ];
    rows.push(headerRow(headers));

    const dataRows: RebateExportRow[] = table.rows.map((row) =>
      dataRow([
        textCell(row.label),
        ...columnsToInclude.map((colIndex) =>
          cellFromReportTableCell(
            row.cells[colIndex],
            table.columnHeaders[colIndex] ?? '',
          ),
        ),
      ]),
    );
    rows.push(...dataRows);

    if (includeTotals && table.rows.length > 0) {
      const totals: RebateExportCell[] = [textCell('Total')];
      columnsToInclude.forEach((colIndex, arrayIndex) => {
        const header = table.columnHeaders[colIndex] ?? '';
        const sum = sumNumericColumn(dataRows, arrayIndex + 1);
        if (sum === null) {
          totals.push({ value: 0, kind: 'number' });
          return;
        }
        totals.push({ value: sum, kind: kindForNumericHeader(header) });
      });
      rows.push(dataRow(totals));
    }
  });

  return rows;
}

export function buildExportRowsFromRecords({
  data,
  columns,
  includeTotals = true,
}: DownloadCSVOptions): RebateExportRow[] {
  if (!data || data.length === 0) {
    return [];
  }

  let records = data;
  if (columns && columns.length > 0) {
    records = data.map((row) => {
      const filteredRow: Record<string, unknown> = {};
      columns.forEach((column) => {
        filteredRow[column.label] = unwrapDownloadCsvCell(row[column.id]);
      });
      return filteredRow;
    });
  }

  const headers = Object.keys(records[0] ?? {});
  const rows: RebateExportRow[] = [headerRow(headers)];
  const dataRows = records.map((row) =>
    dataRow(
      headers.map((header) => {
        const parsed = parseRebateExportCell(row[header]);
        if (parsed.kind === 'number') {
          const raw = row[header];
          if (typeof raw === 'string' && isCurrencyFormatted(raw)) {
            return { ...parsed, kind: 'currency' };
          }
          return { ...parsed, kind: kindForNumericHeader(header) };
        }
        return parsed;
      }),
    ),
  );
  rows.push(...dataRows);

  if (includeTotals && records.length > 0) {
    const totals: RebateExportCell[] = headers.map((header, index) => {
      if (index === 0) {
        return textCell('Total');
      }
      const sum = sumNumericColumn(dataRows, index);
      if (sum === null) {
        return emptyCell();
      }
      return { value: sum, kind: kindForNumericHeader(header) };
    });
    rows.push(dataRow(totals));
  }

  return rows;
}

export function exportRowsFromCsvMatrix(matrix: string[][]): RebateExportRow[] {
  return matrix.map((row, rowIndex) => {
    if (rowIndex === 0) {
      return headerRow(row);
    }
    return dataRow(
      row.map((value, columnIndex) => {
        if (columnIndex === 0) {
          return textCell(value);
        }
        const parsed = parseRebateExportCell(value);
        if (parsed.kind === 'number') {
          const header = matrix[0]?.[columnIndex] ?? '';
          return { ...parsed, kind: kindForNumericHeader(header) };
        }
        return parsed;
      }),
    );
  });
}

export function buildExportRowsFromCsvString(csv: string): RebateExportRow[] {
  const withoutBom = csv.replace(/^\uFEFF/, '');
  const parsed = Papa.parse<string[]>(withoutBom, {
    header: false,
    skipEmptyLines: false,
  });
  const matrix = parsed.data.filter((row, index, rows) => {
    if (index === rows.length - 1 && row.length === 1 && row[0] === '') {
      return false;
    }
    return true;
  });
  return exportRowsFromCsvMatrix(matrix);
}

export function rebateExportRowsToStringMatrix(
  rows: RebateExportRow[],
): string[][] {
  return rows.map((row) =>
    row.cells.map((cell) => {
      if (cell.kind === 'empty' || cell.value === null) {
        return '';
      }
      if (cell.value instanceof Date) {
        return cell.value.toISOString().slice(0, 10);
      }
      return String(cell.value);
    }),
  );
}
